<template>
  <div class="payment-rolling-split" data-testid="payment-sidebar-split">
    <b-alert
      v-if="view.alert"
      show
      :variant="view.alert.variant"
      class="mb-2"
      data-testid="payment-sidebar-split-alert"
    >
      <h4 class="alert-heading d-flex align-items-center">
        <feather-icon icon="AlertTriangleIcon" size="14" class="mr-50" />
        {{ view.alert.title }}
      </h4>
      <div class="alert-body">
        {{ view.alert.text }}
      </div>
    </b-alert>

    <!-- Transferências: o que o Financeiro executa vem primeiro. -->
    <div class="mb-1">
      <b>Transferências</b>
    </div>
    <div class="split-row">
      <span>Solicitante:</span>
      <b class="text-primary text-right">{{ bill.consultant.name }}</b>
    </div>
    <div class="split-row">
      <span>Tipo contrato:</span>
      <span>Consultor</span>
    </div>
    <div class="split-row">
      <span>Data da solicitação:</span>
      <span>{{ bill.created_at | onlyDate }}</span>
    </div>
    <div class="split-row">
      <span>Chave Pix:</span>
      <span class="d-flex align-items-center">
        <span class="text-break">{{ bill.consultant.franchise_pix }}</span>
        <b-button
          v-if="!readonly && bill.consultant.franchise_pix"
          variant="flat-secondary"
          size="sm"
          class="btn-icon ml-25"
          :aria-label="copied === 'pix' ? 'Chave Pix copiada' : 'Copiar chave Pix'"
          :title="copied === 'pix' ? 'Copiado' : 'Copiar chave Pix'"
          data-testid="payment-sidebar-split-pix-copy"
          @click="copy('pix', String(bill.consultant.franchise_pix))"
        >
          <feather-icon :icon="copied === 'pix' ? 'CheckIcon' : 'CopyIcon'" size="14" />
        </b-button>
      </span>
    </div>
    <div class="split-row">
      <span>Tipo da chave:</span>
      <span class="text-capitalize">
        {{ bill.consultant.franchise_pix ? bill.consultant.franchise_pix_type_key : "" }}
      </span>
    </div>

    <div
      v-for="transfer in transfers"
      :key="transfer.key"
      class="border rounded px-1 py-75 mb-75"
      :data-testid="`payment-sidebar-split-transfer-${transfer.key}`"
    >
      <div class="d-flex align-items-center">
        <b-badge pill :variant="transfer.variant" class="mr-50">{{ transfer.number }}</b-badge>
        <b class="flex-grow-1">{{ transfer.name }}</b>
        <b-badge v-if="transfer.badge" variant="light-warning" class="mr-50">{{ transfer.badge }}</b-badge>
        <b
          v-if="transfer.amount !== null"
          class="split-amount"
          :data-testid="`payment-sidebar-split-transfer-${transfer.key}-amount`"
        >
          {{ transfer.amount | toCurrency }}
        </b>
        <small v-else>Não calculada</small>
        <b-button
          v-if="!readonly && transfer.amount > 0"
          variant="flat-secondary"
          size="sm"
          class="btn-icon ml-25"
          :aria-label="copied === transfer.key ? 'Valor copiado' : `Copiar valor da transferência da ${transfer.name}`"
          :title="copied === transfer.key ? 'Copiado' : 'Copiar valor'"
          :data-testid="`payment-sidebar-split-transfer-${transfer.key}-copy`"
          @click="copy(transfer.key, copyValue(transfer.amount))"
        >
          <feather-icon :icon="copied === transfer.key ? 'CheckIcon' : 'CopyIcon'" size="14" />
        </b-button>
      </div>
      <small class="d-block split-indent">{{ transfer.cnpj }}</small>
      <small
        v-for="line in transfer.lines"
        :key="line"
        class="d-block split-indent text-warning"
      >
        {{ line }}
      </small>
    </div>

    <div class="border rounded px-1 py-75 mb-2" data-testid="payment-sidebar-split-transfers-sum">
      <div class="d-flex justify-content-between align-items-baseline">
        <b>Soma das transferências</b>
        <b v-if="view.transfers.sum !== null" class="split-amount">{{ view.transfers.sum | toCurrency }}</b>
        <small v-else>Não calculada</small>
      </div>
      <small v-if="!view.divergent" class="d-flex align-items-center text-success">
        <feather-icon icon="CheckCircleIcon" size="12" class="mr-25" />
        Igual ao {{ view.amountLabel }}
      </small>
      <small v-else class="d-flex align-items-center text-danger">
        <feather-icon icon="XCircleIcon" size="12" class="mr-25" />
        O Total da fatura ({{ view.invoiceTotal | toCurrency }}) é diferente do {{ view.amountLabel }}
        ({{ view.approvedAmount | toCurrency }}).
      </small>
    </div>

    <!-- Detalhes da fatura: de onde vem cada transferência. -->
    <div class="border-top pt-1 mb-1">
      <b>Detalhes da fatura</b>
    </div>

    <div class="mb-2" data-testid="payment-sidebar-split-platform">
      <div class="d-flex align-items-center mb-50">
        <b-badge variant="light-info" class="mr-50">Futuro Plataforma</b-badge>
        <small>CNPJ 48.312.560/0001-08</small>
      </div>
      <div class="split-row">
        <span>Total de entrevista paga:</span>
        <span class="split-amount">{{ view.platform.interviews | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>Total de assinatura:</span>
        <span class="split-amount">{{ view.platform.subscriptions | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>Ajuste plataforma Futuro:</span>
        <span class="split-amount">{{ view.platform.adjustments | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>Rolagem e outros ajustes:</span>
        <span class="split-amount" data-testid="payment-sidebar-split-platform-rolling">
          {{ view.platform.rolling | toCurrency }}
        </span>
      </div>
      <b-link
        v-if="view.platform.composition.count"
        class="d-inline-flex align-items-center mb-50"
        :aria-expanded="open.platform ? 'true' : 'false'"
        @click="open.platform = !open.platform"
      >
        <small>{{ open.platform ? "Ocultar composição" : `Ver composição (${view.platform.composition.label})` }}</small>
        <feather-icon :icon="open.platform ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="12" class="ml-25" />
      </b-link>
      <div v-if="open.platform" class="split-composition mb-75" data-testid="payment-sidebar-split-platform-items">
        <div class="d-flex flex-wrap align-items-center mb-50">
          <b-badge variant="light-info" class="mr-50">Futuro Plataforma</b-badge>
          <small class="mr-50">{{ view.platform.composition.label }}</small>
          <small class="split-amount font-weight-bolder ml-auto">{{ view.platform.rolling | toCurrency }}</small>
        </div>
        <rolling-item
          v-for="(item, index) in view.platform.composition.list"
          :key="`p-${index}`"
          :item="item"
        />
        <small v-if="view.platform.composition.hidden" class="d-block mb-50">
          Mostrando {{ view.platform.composition.list.length }} de {{ view.platform.composition.count }} lançamentos.
          A soma considera todos.
        </small>
        <small class="d-flex text-success">
          <feather-icon icon="CheckCircleIcon" size="12" class="mr-25 mt-25 flex-shrink-0" />
          {{ view.platform.composition.footer }}
        </small>
      </div>
      <div class="split-row border-top pt-50">
        <b>Total plataforma Futuro:</b>
        <b class="split-amount" :class="{ 'text-danger': view.platform.negative }">
          {{ view.platform.total | toCurrency }}
        </b>
      </div>
      <small v-if="view.platform.compensated" class="d-block text-warning">
        Compensado na transferência da Futuro Hub.
      </small>
    </div>

    <div class="mb-2" data-testid="payment-sidebar-split-hub">
      <div class="d-flex align-items-center mb-50">
        <b-badge variant="light-secondary" class="mr-50">Futuro Hub</b-badge>
        <small>CNPJ 40.411.705/0001-16</small>
      </div>
      <div class="split-row">
        <span>Total de solução financeira:</span>
        <span class="split-amount">{{ view.hub.solutions | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>Ajuste Futuro Hub:</span>
        <span class="split-amount">{{ view.hub.adjustments | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>Rolagem e outros ajustes:</span>
        <span
          class="split-amount"
          :class="{ 'text-danger': view.hub.rollingNegative }"
          data-testid="payment-sidebar-split-hub-rolling"
        >
          {{ view.hub.rolling | toCurrency }}
        </span>
      </div>
      <small v-if="view.hub.hasUnassigned" class="d-flex align-items-center text-warning mb-50">
        <feather-icon icon="InfoIcon" size="12" class="mr-25" />
        inclui {{ view.hub.unassigned | toCurrency }} sem empresa pagadora
      </small>
      <b-link
        v-if="hubItemsCount"
        class="d-inline-flex align-items-center mb-50"
        :aria-expanded="open.hub ? 'true' : 'false'"
        @click="open.hub = !open.hub"
      >
        <small>{{ open.hub ? "Ocultar composição" : `Ver composição (${view.hub.composition.label})` }}</small>
        <feather-icon :icon="open.hub ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="12" class="ml-25" />
      </b-link>
      <div v-if="open.hub" class="split-composition mb-75" data-testid="payment-sidebar-split-hub-items">
        <template v-for="group in hubGroups">
          <div :key="`${group.key}-header`" class="d-flex flex-wrap align-items-center mb-50">
            <b-badge :variant="group.variant" class="mr-50">{{ group.name }}</b-badge>
            <small class="mr-50">{{ group.composition.label }}</small>
            <small class="split-amount font-weight-bolder ml-auto">{{ group.composition.total | toCurrency }}</small>
          </div>
          <rolling-item
            v-for="(item, index) in group.composition.list"
            :key="`${group.key}-${index}`"
            :item="item"
          />
          <small v-if="group.composition.hidden" :key="`${group.key}-hidden`" class="d-block mb-50">
            Mostrando {{ group.composition.list.length }} de {{ group.composition.count }} lançamentos.
            A soma considera todos.
          </small>
        </template>
        <small class="d-flex text-success">
          <feather-icon icon="CheckCircleIcon" size="12" class="mr-25 mt-25 flex-shrink-0" />
          {{ view.hub.composition.footer }}
        </small>
      </div>
      <div class="split-row border-top pt-50">
        <b>Total Futuro Hub:</b>
        <b class="split-amount" :class="{ 'text-danger': view.hub.negative }">
          {{ view.hub.total | toCurrency }}
        </b>
      </div>
      <small v-if="view.hub.compensated" class="d-block text-warning">
        Compensado na transferência da Futuro Plataforma.
      </small>
    </div>

    <div class="border-top pt-50 mb-2">
      <div class="split-row mb-0">
        <b>Total da fatura:</b>
        <b class="split-amount" data-testid="payment-sidebar-split-invoice-total">
          {{ view.invoiceTotal | toCurrency }}
        </b>
      </div>
      <small class="d-block">{{ view.rollingCaption }}</small>
    </div>

    <!-- Conferência -->
    <div class="border-top pt-1 mb-1">
      <b>Conferência</b>
    </div>
    <div class="mb-1">
      <div class="mb-50">Nota fiscal da Futuro Hub</div>
      <div v-if="view.hubInvoiceAmount !== null" class="split-row">
        <span>Valor esperado na nota:</span>
        <b class="split-amount">{{ view.hubInvoiceAmount | toCurrency }}</b>
      </div>
      <b-button
        v-if="bill.document_url && canDownloadDocument"
        variant="link"
        size="md"
        no-caret
        style="padding: 0"
        @click="$emit('download')"
      >
        Ver nota fiscal <feather-icon icon="DownloadIcon" />
      </b-button>
    </div>
    <div class="mb-1">
      <div class="split-row">
        <span>Total da fatura:</span>
        <span class="split-amount">{{ view.invoiceTotal | toCurrency }}</span>
      </div>
      <div class="split-row">
        <span>{{ view.amountLabel }}:</span>
        <b class="split-amount text-primary">{{ view.approvedAmount | toCurrency }}</b>
      </div>
      <small
        class="d-flex align-items-center"
        :class="view.conference.equal ? 'text-success' : 'text-danger font-weight-bolder'"
        data-testid="payment-sidebar-split-conference"
      >
        <feather-icon :icon="view.conference.equal ? 'CheckCircleIcon' : 'XCircleIcon'" size="12" class="mr-25" />
        {{ view.conference.text }}
      </small>
    </div>
    <div v-if="canOpenAdjustments" class="mb-1">
      <small class="d-block">Ver lançamentos de ajuste:</small>
      <div class="d-flex flex-wrap">
        <b-link
          v-for="link in adjustmentLinks"
          :key="link.key"
          :to="adjustmentsRoute(link.payingCompanyId)"
          target="_blank"
          class="mr-1"
          :title="link.title"
          :data-testid="`payment-sidebar-split-adjustments-link-${link.key}`"
        >
          <small>
            {{ link.name }}
            <feather-icon icon="ExternalLinkIcon" size="12" />
          </small>
        </b-link>
      </div>
    </div>
  </div>
</template>

<script>
import { BAlert, BBadge, BButton, BLink } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { HUB_ID, NONE_FILTER, PLATFORM_ID } from "@/constants/paying_companies";
import { formatCopy, formatCurrency } from "../utils/rollingSplit";

const RollingItem = {
  functional: true,
  props: { item: { type: Object, required: true } },
  render(h, { props }) {
    const { item } = props;
    const amount = formatCurrency(item.amount);
    return h("div", { class: "d-flex justify-content-between align-items-start mb-50 pl-50" }, [
      h("small", { class: "d-flex flex-column mr-1" }, [
        h("span", item.title),
        item.detail ? h("span", { class: "split-detail" }, item.detail) : null,
      ]),
      h("small", { class: ["split-amount", { "text-danger": item.negative }] }, amount),
    ]);
  },
};

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BLink,
    RollingItem,
  },
  props: {
    bill: { type: Object, required: true },
    // Saída de buildRollingSplitView (utils/rollingSplit.js).
    view: { type: Object, required: true },
    canOpenAdjustments: { type: Boolean, default: false },
    adjustmentsRoute: { type: Function, required: true },
    // "Detalhes da solicitação": só consulta, sem os botões de copiar do pagamento.
    readonly: { type: Boolean, default: false },
    canDownloadDocument: { type: Boolean, default: true },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      open: { platform: false, hub: false },
      copied: null,
    };
  },
  computed: {
    transfers() {
      return [
        {
          key: "platform",
          number: "1",
          name: "Futuro Plataforma",
          cnpj: "CNPJ 48.312.560/0001-08",
          variant: "light-info",
          amount: this.view.transfers.platform,
          badge: this.view.transfers.platformBadge,
          lines: this.view.transfers.platformLines,
        },
        {
          key: "hub",
          number: "2",
          name: "Futuro Hub",
          cnpj: "CNPJ 40.411.705/0001-16",
          variant: "light-secondary",
          amount: this.view.transfers.hub,
          badge: this.view.transfers.hubBadge,
          lines: this.view.transfers.hubLines,
        },
      ];
    },
    hubGroups() {
      const { assigned, unassigned } = this.view.hub.composition;
      return [
        { key: "hub", name: "Futuro Hub", variant: "light-secondary", composition: assigned },
        { key: "unassigned", name: "Sem empresa pagadora → Futuro Hub", variant: "light-warning", composition: unassigned },
      ].filter((group) => group.composition.count > 0);
    },
    hubItemsCount() {
      return this.view.hub.composition.assigned.count + this.view.hub.composition.unassigned.count;
    },
    adjustmentLinks() {
      const title = (company) =>
        `Abre a lista de ajustes filtrada por este PN e pela ${company}. A lista não tem filtro de competência.`;
      const links = [
        { key: "platform", name: "Futuro Plataforma", payingCompanyId: PLATFORM_ID, title: title("Futuro Plataforma") },
        { key: "hub", name: "Futuro Hub", payingCompanyId: HUB_ID, title: title("Futuro Hub") },
      ];
      if (this.view.hub.hasUnassigned) {
        links.push({
          key: "unassigned",
          name: "Sem empresa pagadora",
          payingCompanyId: NONE_FILTER,
          title: "Abre a lista de ajustes deste PN sem empresa pagadora. A lista não tem filtro de competência.",
        });
      }
      return links;
    },
  },
  methods: {
    copyValue(amount) {
      return formatCopy(amount);
    },
    copy(key, text) {
      this.$copyText(text).then(
        () => {
          this.copied = key;
          setTimeout(() => {
            if (this.copied === key) this.copied = null;
          }, 2000);
        },
        () => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Não foi possível copiar. Selecione o valor e copie manualmente.",
              icon: "AlertTriangleIcon",
              variant: "warning",
            },
          });
        }
      );
    },
  },
};
</script>

<style lang="scss">
// Sem `scoped`: o item da composição é um componente funcional e não herda o atributo de escopo.
.payment-rolling-split {
  .split-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 0.5rem;

    > :first-child {
      margin-right: 1rem;
    }
  }

  .split-amount {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .split-indent {
    padding-left: 1.9rem;
  }

  .split-composition {
    border-left: 2px solid rgba(130, 134, 139, 0.3);
    padding-left: 0.75rem;
  }

  .split-detail {
    opacity: 0.8;
  }
}
</style>
